import React, { useContext, useEffect, useState } from "react";
import { NavObjectType, lddGlobalSessionInfo } from "../navigationTypes";
import {
  Offcanvas,
  ListGroup,
  Modal,
  Nav,
  OverlayTrigger,
  Tooltip,
  Alert,
  Button,
  Container,
  Row,
  Col,
  Form
} from "react-bootstrap";
import BaseTopBar from "./BaseTopBar";
import styles from "../mainbar.module.css";
import { NavigationContext } from "../../Context/NavigationContext";
import { Notifications } from "../Notifications";
import { useAppSelector } from "../../redux/hooks";
import ClientConnectFirmSettings from "../Settings/ClientConnectFirmSettings";
import { useEsignAllowlist } from "../clientportal/components/useEsignAllowlist";
import { topBarConfig } from "./topBarConfig";
import UpdatePasswordModal from "../UpdatePassword/UpdatePasswordModal";
import { setTheme } from "./topBarFuncs";
import TaskList from "../Settings/TaskManagementSettings/TaskList";
import CalendarHome from "../Calendar/CalendarHome";
import { lddStateObject, setUserPreferences } from "../../redux/reducer";
import { useDispatch } from "react-redux";
import { displayToastMsg } from "../toastMessages/toastmessages";
import UndertakingSettings from "../Settings/UndertakingSettings/UndertakingSettings";
import { useEsignAuth } from "../Esign/useEsignAuth";
import { getCachedSubForSid } from "../Esign/oidcManager";

/**
 * Append `?account=<sub>` to an esign SPA URL when the active sub is known.
 * Without it, the SPA boots into the AccountPicker if more than one UV-cookie
 * account is live in the browser. Mirrors `withAccount` in ESigningCard.tsx.
 */
function withEsignAccount(url: string, sid: string): string {
  const sub = getCachedSubForSid(sid);
  if (!sub) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}account=${encodeURIComponent(sub)}`;
}

export const OpenReports: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formxml: "SingleReport",
    onload: "report_init",
    src: "getprofile.asp?pname=ReportProfile",
  },
};
export const OpenUserSettings: NavObjectType = {
  component: "main",
  url: "lfpopup.asp",
  popup: true,
  isModal: true,
  popupStyle: "xlargelong",
  params: { formasp: "EditUserCredentials" },
};
const OpenFirmProfile: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection: "profile_SectionTable_Head,profile_SectionTable",
  },
};
const OpenFirmProfileStmtInterim: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection: "InterimAccountDefault_SectionTable_Header,InterimAccountDefault_SectionTable",
  },
};
const OpenFirmProfileStmt: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection: "AccountDefault_SectionTable_Head,AccountDefault_SectionTable",
  },
};
const OpenFirmProfileTrust: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection: "TrustDefault_SectionTable_Head,TrustDefault_SectionTable",
  },
};
const OpenFirmProfileFinancial: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection:
      "FinancialDefaults_SectionTable,FinancialDefaults_SectionTable_Head",
  },
};
const OpenFirmProfileCosts: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection:
      "EstimatedDefault_SectionTable,EstimatedDefault_SectionTable_Head",
  },
};
const OpenFirmProfileDocs: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection: "other_SectionTable_Head,other_SectionTable",
  },
};
const OpenFirmProfileAccount: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection: "TrustAccount_SectionTable_Head,TrustAccount_SectionTable",
  },
};
const OpenFirmProfile3rdParty: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formasp: "EditUserProfile",
    goToSection: "3rdParty_SectionTable_Head,3rdParty_SectionTable",
  },
};
const OpenUserFields: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formxml: "UserDefinedFields",
    src: "getprofile.asp?pname=UserDefinedFields",
  },
};
// const OpenCalendarUserProfile: NavObjectType = {
//   component: "main",
//   url: "lfmaster.asp",
//   popup: false,
//   params: {
//     formxml: "CalendarUserProfile",
//     onload: "cal_init",
//     src: "getCalendarProfile.asp?pname=CalendarProfile",
//   },
// };
const OpenFileTemplates: NavObjectType = {
  component: "main",
  url: "DisplayList.asp",
  popup: false,
  params: { ListName: "AllFileTemplates" },
};
const OpenFileListColumns: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: {
    formxml: "MainListDefine",
    src: "getprofile.asp?pname=MainListDefine",
  },
};
const OpenDocTemplates: NavObjectType = {
  component: "main",
  url: "DisplayList.asp",
  popup: false,
  params: { ListName: "AllTemplateTypes" },
};
const OpenMunicipalitiesAll: NavObjectType = {
  component: "main",
  url: "DisplayList.asp",
  popup: false,
  params: { ListName: "municAll" },
};
const OpenMunicipalitiesCustomized: NavObjectType = {
  component: "main",
  url: "tableBrowser.asp",
  popup: false,
  params: { name: "municUser" },
};
const OpenBilling: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: { formasp: "ShowAccountStatement" },
};
const OpenPayments: NavObjectType = {
  component: "main",
  url: "lfmaster.asp",
  popup: false,
  params: { formasp: "ShowAccountStatement", adhoc: "1" },
};
const OpenBuilderSale: NavObjectType = {
  component: "main",
  url: "purFileListBuilder.asp",
  popup: false,
};
const OpenDocMasters: NavObjectType = {
  component: "docmasters",
  reactFrame: "docmasters",
};
const OpenPackages: NavObjectType = {
  component: "docmasters",
  reactFrame: "packages",
};
const OpenCalendar: NavObjectType = {
  component: "main",
  url: "calendar.asp",
  popup: true,
  popupStyle: "xlarge",
  isModal: true,
  params: { compat: "1", viewTab: "3", NoHeader: "1" },
};

function SidebarTitle(title: string, _icon: string) {
  return <h4>{title}</h4>;
}
function SidebarAction(
  title: string,
  icon: string,
  click: NavObjectType,
  sid: string,
  nav: (navObject: NavObjectType) => void,
  closeSidebar: () => void,
  disable: boolean,
  isAdmin = false,
  setShowAdminAlert?: (showAdminAlert: boolean) => void
) {
  const onClick = () => {
    switch (title) {
      case "Masters":
      case "Packages":
        if (isAdmin) {
          nav(click);
          closeSidebar();
        } else {
          setShowAdminAlert(true);
        }
        break;
      default:
        nav(click);
        closeSidebar();
    }
  };
  return (
    <ListGroup.Item
      disabled={disable}
      className={styles.menuItem}
      action
      onClick={() => {
        onClick();
      }}
    >
      <i className={"fa-light fa-" + icon}></i>
      {title}
    </ListGroup.Item>
  );
}

interface ESigningMenuItemProps {
  apiKey: string;
  sid: string;
  parentShow: boolean;
}

const ESigningPreferences = ({ apiKey, sid, parentShow }: ESigningMenuItemProps) => {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(!show);
  useEffect(() => {
    if (!parentShow) setShow(false);
  }, [parentShow]);
  // Bootstrap esign OIDC tokens (initEsignSSO) when the modal opens so the
  // es-<sub> partition exists and getCachedSubForSid(sid) resolves. Without
  // it the SPA has no login partition on first load and detours through the
  // AccountPicker + OIDC redirect, landing on its root "fails" page.
  const { jwt } = useEsignAuth(sid, show);
  return (
    <>
      <ListGroup.Item className={styles.menuItem} action onClick={handleShow}>
        <i className="fa-light fa-sliders da-indicator"></i>eSigning Preferences
      </ListGroup.Item>
      <Modal
        show={show}
        onHide={handleClose}
        className="modal-settings modal-list"
        backdrop={false}
      >
        {jwt ? (
          <iframe
            title="eSigning Preferences"
            src={withEsignAccount(`/esignportal/settings/preferences?apiKey=${encodeURIComponent(apiKey)}&source=cc`, sid)}
            width="100%"
            height="100%"
            onLoad={() => {
              setTheme();
            }}
          />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 360, color: "#6b7280" }}>
            <i className="fa-solid fa-arrows-rotate fa-spin" style={{ marginRight: 8 }}></i>
            Preparing eSigning…
          </div>
        )}
      </Modal>
    </>
  );
};

const ESigningEmails = ({ apiKey, sid, parentShow }: ESigningMenuItemProps) => {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(!show);
  useEffect(() => {
    if (!parentShow) setShow(false);
  }, [parentShow]);
  // Bootstrap esign OIDC tokens (initEsignSSO) when the modal opens so the
  // es-<sub> partition exists and getCachedSubForSid(sid) resolves. Without
  // it the SPA has no login partition on first load and detours through the
  // AccountPicker + OIDC redirect, landing on its root "fails" page.
  const { jwt } = useEsignAuth(sid, show);
  return (
    <>
      <ListGroup.Item className={styles.menuItem} action onClick={handleShow}>
        <i className="fa-light fa-envelope da-indicator"></i>eSigning Emails
      </ListGroup.Item>
      <Modal
        show={show}
        onHide={handleClose}
        className="modal-settings modal-list"
        backdrop={false}
      >
        {jwt ? (
          <iframe
            title="eSigning Email Settings"
            src={withEsignAccount(`/esignportal/settings/email-templates?apiKey=${encodeURIComponent(apiKey)}&source=cc`, sid)}
            width="100%"
            height="100%"
            onLoad={() => {
              setTheme();
            }}
          />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 360, color: "#6b7280" }}>
            <i className="fa-solid fa-arrows-rotate fa-spin" style={{ marginRight: 8 }}></i>
            Preparing eSigning…
          </div>
        )}
      </Modal>
    </>
  );
};

function ShowSettingsCanvas() {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const SessionInfo: lddGlobalSessionInfo = useAppSelector(
    (state) => state.SessionInfo
  );
  const sid = SessionInfo.sid;
  // Gates the two eSigning TopBar menu items on the BetaUser.json "Esign"
  // list, which matches firm id, province, or appCode.
  const { allowed: esignAllowed } = useEsignAllowlist();
  const isAdmin = SessionInfo.user.isAdmin;
  const hasDocsRight = SessionInfo.user.userRights.some(
    (right) =>
      right.name === `docs${SessionInfo.appCode ?? "RPW"}` && right.enabled
  );
  const [showAdminAlert, setShowAdminAlert] = useState(false);
  const nav = useContext(NavigationContext);
  const isCollege: boolean = SessionInfo.firm?.IsCollege;
  const isCollegeDefaultAdmin: boolean = SessionInfo.firm?.IsCollegeDefaultAdmin;

  const ManageUsers = (sid: string) => {
    useEffect(() => {
      if (!show) {
        setShowManageUsers(false);
      }
    }, [show]);

    const [showManageUsers, setShowManageUsers] = useState(false);
    const handleClose = () => setShowManageUsers(false);
    const handleShow = () => setShowManageUsers(true);
    return (
      <>
        <ListGroup.Item className={styles.menuItem} action onClick={handleShow}>
          <i className="fa-light fa-user-gear da-indicator"></i>Manage Users
        </ListGroup.Item>
        <Modal
          show={showManageUsers}
          onHide={handleClose}
          className="modal-settings modal-list"
          backdrop={false}
        >
          <iframe
            title="User Management"
            src={`${window.location.origin}/${process.env.REACT_APP_UM_ENDPOINT}/index.html?SessionID=${sid}`}
            width="100%"
            height="100%"
            onLoad={() => {
              setTheme();
            }}
          />
        </Modal>
      </>
    );
  };

  const UserProfile = (sid: string) => {
    useEffect(() => {
      if (!show) {
        setShowUserProfile(false);
      }
    }, [show]);

    const [showUserProfile, setShowUserProfile] = useState(false);
    const handleClose = () => setShowUserProfile(false);
    const handleShow = () => setShowUserProfile(true);

    // Get current user's ID and SubUserID from SessionInfo
    const currentUserId = SessionInfo.firm?.id || '';
    const currentSubUserId = SessionInfo.user?.id || '';

    return (
      <>
        <ListGroup.Item className={styles.menuItem} action onClick={handleShow}>
          <i className="fa-light fa-user-gear da-indicator"></i>User Profile
        </ListGroup.Item>
        <Modal
          show={showUserProfile}
          onHide={handleClose}
          className="modal-settings modal-list"
          backdrop={false}
        >
          <iframe
            title="User Profile"
            src={`${window.location.origin}/${process.env.REACT_APP_UM_ENDPOINT}/index.html?SessionID=${sid}#/EditUser/fromStaff?UserID=${currentUserId}&SubUserID=${currentSubUserId}`}
            width="100%"
            height="100%"
            onLoad={() => {
              setTheme();
            }}
          />
        </Modal>
      </>
    );
  };

  return (
    <>
      {showAdminAlert && (
        <Modal
          show={showAdminAlert}
          onHide={() => setShowAdminAlert(false)}
          backdrop="static"
          keyboard={false}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Administrator Permission Required</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning">
              <p>
                You do not have <b>Administrator permission</b> to modify these
                settings. Please ask your Account Administrator to make any
                changes you require.
              </p>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowAdminAlert(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Nav.Item className={styles.item}>
        <OverlayTrigger
          placement="bottom"
          delay={200}
          overlay={<Tooltip>Settings</Tooltip>}
        >
          <Nav.Link eventKey="link-4">
            <i
              className="fa-duotone fa-gear fa-indicator"
              onClick={handleShow}
            ></i>
          </Nav.Link>
        </OverlayTrigger>
      </Nav.Item>

      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="end"
        className="settings-menu"
        scroll
        style={{zIndex: 2000}}
      >
        <Offcanvas.Header closeButton>
          <h2>
            <i className="fa-duotone fa-gear"></i>Settings
          </h2>
        </Offcanvas.Header>

        <Offcanvas.Body className={styles.settingsTabs}>
          {SidebarTitle("Firm Management", "fa-light fa-user fa-indicator")}

          <ListGroup variant="flush">
            {SidebarAction(
              "Firm Information",
              "city",
              OpenFirmProfile,
              sid,
              nav,
              handleClose,
              isCollege
            )}
            {((isAdmin && !isCollege) || isCollegeDefaultAdmin) &&
              ManageUsers(sid)}
            {!isAdmin && !isCollegeDefaultAdmin &&
              UserProfile(sid)}
            <ListGroup.Item
              className={styles.menuItem}
              action
              onClick={() =>
                window.open(
                  `${process.env.REACT_APP_VIP_ENDPOINT}/LDDUserSetting.asp?SessionID=${sid}&Lawyer=`,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              <i className={"fa-light fa-user-edit"}></i> Lawyer Setup
            </ListGroup.Item>
            {(SessionInfo.appCode === "RPW" ||
              SessionInfo.appCode === "RPWBC" ||
              SessionInfo.appCode == "RPWAB") &&
              SidebarAction(
                "Connected Accounts",
                "building-user",
                OpenFirmProfile3rdParty,
                sid,
                nav,
                handleClose,
                isCollege
              )}
            {SidebarAction(
              SessionInfo.appCode === "RPWBC"
                ? "Trust Accounts"
                : "Trust Account",
              "hand-holding-dollar",
              OpenFirmProfileAccount,
              sid,
              nav,
              handleClose,
              isCollege
            )}
            {SidebarAction(
              "Legacy Calendar",
              "calendar-days",
              OpenCalendar,
              sid,
              nav,
              handleClose,
              false
            )}
          </ListGroup>

          {SidebarTitle("Financial", "fa-light fa-city fa-indicator")}

          <ListGroup variant="flush">
            {SessionInfo.appCode === "RPW" && 
              SidebarAction(
                "Account to Client - Interim",
                "file-user",
                OpenFirmProfileStmtInterim,
                sid,
                nav,
                handleClose,
                isCollege
            )}
            {SidebarAction(
              "Account to Client",
              "file-user",
              OpenFirmProfileStmt,
              sid,
              nav,
              handleClose,
              isCollege
            )}
            {SessionInfo.appCode === "RPWMB" &&
              SidebarAction(
                "Estimated Closing Costs Defaults",
                "file-invoice-dollar",
                OpenFirmProfileCosts,
                sid,
                nav,
                handleClose,
                isCollege
              )}
            {(SessionInfo.appCode === "RPWBC" ||
              SessionInfo.appCode === "RPWMB" ||
              SessionInfo.appCode === "RPWSK" ||
              SessionInfo.appCode === "RPWNS") &&
              SidebarAction(
                "Financial Defaults",
                "folder-gear",
                OpenFirmProfileFinancial,
                sid,
                nav,
                handleClose,
                isCollege
              )}
            {SessionInfo.appCode !== "RPWBC" &&
              SessionInfo.appCode !== "RPWSK" &&
              SidebarAction(
                "Trust Statement",
                "file-invoice",
                OpenFirmProfileTrust,
                sid,
                nav,
                handleClose,
                isCollege
              )}
            {SessionInfo.appCode == "RPWSK" &&
              SidebarAction(
                "Estimated & Final Adjustments Defaults",
                "file-invoice",
                OpenFirmProfileTrust,
                sid,
                nav,
                handleClose,
                isCollege
              )}
          </ListGroup>

          {SidebarTitle("Firm Customization", "fa-light fa-city fa-indicator")}

          <ListGroup variant="flush">
            {SidebarAction(
              "File Templates",
              "paste",
              OpenFileTemplates,
              sid,
              nav,
              handleClose,
              false
            )}
            {SidebarAction(
              "Custom Fields",
              "pen-field",
              OpenUserFields,
              sid,
              nav,
              handleClose,
              false
            )}
            {SidebarAction(
              "File List Preferences",
              "line-columns",
              OpenFileListColumns,
              sid,
              nav,
              handleClose,
              isCollege
            )}
            {/* {SidebarAction(
              "Calendar Preferences",
              "calendar-days",
              OpenCalendarUserProfile,
              sid,
              nav,
              handleClose,
              false
            )} */}
            <TaskList/>
            <UndertakingSettings className={styles.menuItem}/>
          </ListGroup>

          {SidebarTitle("Connect", "fa-light fa-city fa-indicator")}

          <ListGroup variant="flush">
            {/* {(SessionInfo.appCode === "RPW") && <ClientConnectFirmSettings />} */}
            {<ClientConnectFirmSettings isCollege={isCollege} />}
            {esignAllowed && (
              <ESigningPreferences
                apiKey={(SessionInfo.firm?.id ?? '').toString()}
                sid={sid}
                parentShow={show}
              />
            )}
            {esignAllowed && (
              <ESigningEmails
                apiKey={(SessionInfo.firm?.id ?? '').toString()}
                sid={sid}
                parentShow={show}
              />
            )}
          </ListGroup>

          {SidebarTitle("Documents", "fa-light fa-file-lines fa-indicator")}

          <ListGroup variant="flush">
            <ListGroup.Item
              disabled={false}
              className={styles.menuItem}
              action
              onClick={() => {
                if (isAdmin || hasDocsRight) {
                  nav(OpenDocMasters);
                  handleClose();
                } else {
                  setShowAdminAlert(true);
                }
              }}
            >
              <i className={"fa-light fa-files"}></i>Masters
            </ListGroup.Item>
            <ListGroup.Item
              disabled={false}
              className={styles.menuItem}
              action
              onClick={() => {
                if (isAdmin || hasDocsRight) {
                  nav(OpenPackages);
                  handleClose();
                } else {
                  setShowAdminAlert(true);
                }
              }}
            >
              <i className={"fa-light fa-folder"}></i>Packages
            </ListGroup.Item>
            {/* {SidebarAction(
              "Legacy Masters",
              "files",
              OpenDocMastersLegacy,
              sid,
              nav,
              handleClose,
              false,
              isAdmin || hasDocsRight,
              setShowAdminAlert
            )} */}
            {SidebarAction(
              "Templates",
              "books",
              OpenDocTemplates,
              sid,
              nav,
              handleClose,
              isCollege
            )}
            {SidebarAction(
              "Options",
              "gears",
              OpenFirmProfileDocs,
              sid,
              nav,
              handleClose,
              isCollege
            )}
          </ListGroup>

          {(SessionInfo.appCode === "RPWAB" ||
            SessionInfo.appCode === "RPWMB" ||
            SessionInfo.appCode === "RPWSK") && (
            <>
              {SidebarTitle("New Home", "fa-light fa-apartment fa-indicator")}

              <ListGroup>
                {SidebarAction(
                  "Manage Project Files",
                  "buildings",
                  OpenBuilderSale,
                  sid,
                  nav,
                  handleClose,
                  isCollege
                )}
              </ListGroup>
            </>
          )}

          {SidebarTitle("Databases", "fa-light fa-list fa-indicator")}

          <ListGroup variant="flush">
            {SessionInfo.appCode === "RPW" || SessionInfo.appCode === "RPWAB"
              ? SidebarAction(
                  "Municipalities",
                  "location-dot",
                  OpenMunicipalitiesAll,
                  sid,
                  nav,
                  handleClose,
                  false
                )
              : SidebarAction(
                  "Municipalities",
                  "location-dot",
                  OpenMunicipalitiesCustomized,
                  sid,
                  nav,
                  handleClose,
                  false
                )}
          </ListGroup>

          {SidebarTitle(
            "Billing & Reports",
            "fa-light fa-file-invoice fa-indicator"
          )}

          <ListGroup variant="flush">
            {SidebarAction(
              "Billing Statements",
              "receipt",
              OpenBilling,
              sid,
              nav,
              handleClose,
              isCollege
            )}
            {SidebarAction(
              "Upcoming Payments",
              "wallet",
              OpenPayments,
              sid,
              nav,
              handleClose,
              isCollege
            )}
            {SidebarAction(
              "Reports",
              "chart-simple",
              OpenReports,
              sid,
              nav,
              handleClose,
              isCollege
            )}
          </ListGroup>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

const UserProfileModal = ({ showModal, onHide }) => {
  const SessionInfo = useAppSelector(state => state.SessionInfo);
  const currentUserId = SessionInfo.firm?.id || '';
  const currentSubUserId = SessionInfo.user?.id || '';
  const [currUsername, setCurrUsername] = useState('');
  
  useEffect(() => {
    if (showModal) {
      // Make API call to get current username
      fetch(`${window.location.origin}/uvapi/api/cur/uv/get/curUser?SessionID=${SessionInfo.sid}`)
        .then(response => response.json())
        .then(data => {
          setCurrUsername(data.result.StationID);
        })
        .catch(error => {
          console.error("Error fetching current username:", error);
        });
    }
  }, [showModal, SessionInfo.sid]);
  
  return (
    <Modal
      show={showModal}
      onHide={onHide}
      size="xl"
      backdrop={true}
      centered
    >
      <Modal.Header closeButton style={{position: 'absolute', right: '10px', top: '0', zIndex: '1000', border: 'none'}}></Modal.Header>
      <Modal.Body className="p-0">
        <iframe
          title=""
          src={`${window.location.origin}/${process.env.REACT_APP_UM_ENDPOINT}/index.html?SessionID=${SessionInfo.sid}#/EditUser/fromStaff?UserID=${currentUserId}&SubUserID=${currentSubUserId}&currUsername=${currUsername}`}
          width="100%"
          height="100%"
          style={{ border: 'none', borderRadius: '12px', display: 'flex', height: '100%', width: '100%' }}
          onLoad={() => {
            setTheme();
          }}
        />
      </Modal.Body>
    </Modal>
  );
};

const Preferences = ({ showModal, onHide, sessionInfo }) => {
  const reduxDefaultFileOpeningScreen = useAppSelector((g: lddStateObject) => g.UserPreferences.defaultFileOpeningScreen);
  const [isSavingUserPreferences, setIsSavingUserPreferences] = useState(false);
  const [defaultFileOpeningScreen, setDefaultFileOpeningScreen] = useState<"Summary" | "Details">("Summary");
  const dispatch = useDispatch();

  useEffect(() => {
    setDefaultFileOpeningScreen(reduxDefaultFileOpeningScreen)
  }, [reduxDefaultFileOpeningScreen]);

  const handleSaveUserPreferences = async () => {
    if (isSavingUserPreferences) return;
    setIsSavingUserPreferences(true);
    try {
        if (!sessionInfo?.appProvinceShort || !sessionInfo?.firm?.id || !sessionInfo?.sid) {
            throw new Error("Missing session info for saving preferences.");
        }
        const rwapi = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${sessionInfo.appProvinceShort}/${sessionInfo.firm.id}`;
        const response = await fetch(`${rwapi}/set/userpreferences?SessionID=${sessionInfo.sid}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                defaultFileOpeningScreen,
            }),
        });
        const payload = await response.json();
        if (!payload?.ok) {
            throw new Error(payload?.error || "Failed to save preferences.");
        }
        dispatch(setUserPreferences({ defaultFileOpeningScreen }));
        // setDefaultFileOpeningScreen(defaultFileOpeningScreen)
        displayToastMsg({ id: null, msg: "User Preferences saved successfully!", type: "success" }, null);
        // setShowUserPreferences(false);
        onHide();
    } catch (error) {
        displayToastMsg({ id: null, msg: "Unable to save preferences. Please try again.", type: "error" }, null);
    } finally {
        setIsSavingUserPreferences(false);
    }
  };

  return <Modal
      show={showModal}
      onHide={onHide}
      centered
  >
      <Modal.Header>
          <Modal.Title>User Preferences</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
          <Container>
              <Row>
                  <Col>
                      <Form.Check
                          type="checkbox"
                          id="summaryTabDefault"
                          label="Set Summary as default file opening screen"
                          checked={defaultFileOpeningScreen === "Summary"}
                          onChange={(e) => {console.log("aiusdg"); setDefaultFileOpeningScreen(e.target.checked ? "Summary" : "Details")}}
                          disabled={isSavingUserPreferences}
                      />
                  </Col>
              </Row>
          </Container>
      </Modal.Body>

      <Modal.Footer>
          <Button
              variant="outline-primary"
              onClick={onHide}
              aria-label="Close User Preferences"
          >
              Cancel
          </Button>

          <Button
              disabled={isSavingUserPreferences}
              onClick={handleSaveUserPreferences}
          >
              {isSavingUserPreferences ? (
                  <>
                      Saving..
                  </>
              )
              :
              (
                  <>
                      Save
                  </>
              )}
          </Button>
      </Modal.Footer>
  </Modal>
}

const TopBar = () => {
  const nav = useContext(NavigationContext);
  const SessionInfo: lddGlobalSessionInfo = useAppSelector(
    (state) => state.SessionInfo
  );
  const page = useAppSelector((state) => state.currentNavigation?.page);
  const OpenMainPage: NavObjectType = { component: "filelist" };
  const OpenCalendar: NavObjectType = {
    component: "main",
    url: "calendar.asp",
    popup: true,
    popupStyle: "xlarge",
    isModal: true,
    params: { compat: "1", viewTab: "3", NoHeader: "1" },
  };
  const OpenInbox: NavObjectType = {
    component: "main",
    popup: true,
    isModal: true,
    reactModalFrame: "drlist",
  };
  const OpenCalendarNew: NavObjectType = {
    component: "calendar",
    reactFrame: "calendar",
  };

  const handleOpenMainPage = () => {
    setShowCalendarNew(false);
    nav(OpenMainPage);
  }

    const [showUpdatePasswordModal, setShowUpdatePasswordModal] = useState(false);
    const [showUserProfileModal, setShowUserProfileModal] = useState(false);
    const [showCalendarNew, setShowCalendarNew] = useState(false);
    const [showUserPreferences, setShowUserPreferences] = useState(false);

    const actions = {
        OpenMainPage: () => handleOpenMainPage(),
        OpenCalendar: () => nav(OpenCalendar),
        OpenCalendarNew: () => setShowCalendarNew(true),
        OpenInbox: () => nav(OpenInbox),
        OpenNotificationCenter: (key) => {
          return <Notifications key={key} />;
        },
        OpenSettings: (key) => {
          return <ShowSettingsCanvas key={key} />;
        },
        OpenUserSettings: () => nav(OpenUserSettings),
        OpenPasswordUpdate: () => setShowUpdatePasswordModal(true),
        OpenUserPreferences: () => setShowUserPreferences(true),
        OpenUserProfile: () => {
            setShowUserProfileModal(true);
            document.body.click();
        },
        IsCollege: () => {
          return SessionInfo.firm?.IsCollege ? "1" : "0";
        },
    }

    return (
        <>
            <BaseTopBar
                topBarConfig={topBarConfig}
                actions={actions}
                sessionInfo={SessionInfo}
                sid={SessionInfo?.sid}
                page={page}
            />
            <UpdatePasswordModal
                show={showUpdatePasswordModal}
                onHide={() => setShowUpdatePasswordModal(false)}
            />
            <UserProfileModal
                showModal={showUserProfileModal}
                onHide={() => setShowUserProfileModal(false)}
            />
            <CalendarHome
                showModal={showCalendarNew}
                closeModal={() => setShowCalendarNew(false)}
            />
            <Preferences  
                showModal={showUserPreferences}
                onHide={() => setShowUserPreferences(false)}
                sessionInfo={SessionInfo}
            />
        </>
    );
};

export default TopBar;